import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { Texture, TextureLoader } from 'three'
import * as THREE from 'three'
import type { Source } from '~/service/experience/sources'
import EventEmitter from '~/service/experience/utils/EventEmitter'
import type { SurfaceCollectionCategoryWithCollections } from '~/types/api'

export default class Resources extends EventEmitter {
  sources: Source[]
  models: { [name: string]: THREE.Mesh }
  textures: { [id: number]: Texture }
  surfaces: SurfaceCollectionCategoryWithCollections
  toLoad: number
  loaded: number
  loaders: {
    gltfLoader: GLTFLoader
    textureLoader: TextureLoader
  }

  constructor(sources: Source[]) {
    super()

    // Options
    this.sources = sources

    // Setup
    this.models = {}
    this.textures = {}
    this.toLoad = this.sources.length
    this.loaded = 0

    this.setLoaders()
  }

  async loadSurfaces() {
    const api = useApi()

    this.surfaces = await api.getSurfaces()
  }

  getModel(modelName: string) {
    if (this.models[modelName]) {
      return this.models[modelName]
    }

    // TODO: Find and load model
  }

  getSurface(id: number) {
    if (this.surfaces === null || this.surfaces === undefined) {
      return undefined
    }

    return Object.values(this.surfaces)
      .map((category) => {
        return category.surfaceCollections
          .map((collection) => collection.surfaces)
          .flat()
      })
      .flat()
      .find((surface) => {
        return surface.nid === id
      })
  }

  loadTexture(textureId: number) {
    const texturePath = this.getSurface(textureId)?.image?.url + '/320x320/FIT'

    const config = useRuntimeConfig()

    this.loaders.textureLoader.load(
      config.public.ldAdminUrl + texturePath,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.name = textureId.toString()
        this.textures[textureId] = texture
        this.trigger('textureLoaded', [textureId])
        this.loaded++

        if (this.loaded === this.toLoad) {
          this.rotateInnerCorners()
          this.trigger('ready')
        }
      },
      undefined,
      () => {}
    )
  }

  getTexture(textureId: number, setTexture?: (texture: Texture) => void) {
    if (this.textures[textureId]) {
      if (setTexture) {
        setTexture(this.textures[textureId])
      }
      this.trigger('textureLoaded', [textureId])
      return this.textures[textureId]
    }

    const config = useRuntimeConfig()

    const texturePath = this.getSurface(textureId)?.image?.url + '/320x320/FIT'

    const texture = this.loaders.textureLoader.load(
      config.public.ldAdminUrl + texturePath,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.name = textureId.toString()
        this.textures[textureId] = texture
        if (setTexture) {
          setTexture(texture)
        }
        this.trigger('textureLoaded', [textureId])
      },
      undefined,
      () => {}
    )
    texture.name = textureId.toString()

    return texture
  }

  setLoaders() {
    this.loaders = {
      gltfLoader: new GLTFLoader(),
      textureLoader: new TextureLoader(),
    }
  }

  startLoading() {
    this.sources.forEach((source: Source) => {
      if (source.type === 'gltfModel') {
        this.loaders.gltfLoader.load(source.path, (file) => {
          this.modelLoaded(source, file)
        })
      }
      if (source.type === 'texture') {
        this.loadTexture(+source.path)
      }
    })
  }

  modelLoaded(source: Source, file: GLTF) {
    this.models[source.name] = file.scene.children[0] as THREE.Mesh
    this.models[source.name].traverse((child) => (child.castShadow = true))
    this.loaded++

    if (this.loaded === this.toLoad) {
      this.rotateInnerCorners()
      this.trigger('ready')
    }
  }

  rotateInnerCorners() {
    const lowBackrestInnerCorner = this.models.lowBackrestInnerCorner
    const highBackrestInnerCorner = this.models.highBackrestInnerCorner
    const pillowCornerInner = this.models.pillowCornerInner
    const pillowCornerOuter = this.models.pillowCornerOuter

    lowBackrestInnerCorner.rotation.y = Math.PI
    highBackrestInnerCorner.rotation.y = Math.PI
    pillowCornerInner.rotation.y = Math.PI
    pillowCornerInner.scale.x *= -1
    pillowCornerOuter.rotation.y = Math.PI
    pillowCornerOuter.scale.z *= -1
  }
}
